import { Suspense, lazy, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Bike, Check, Clock, MapPinOff, Phone } from "lucide-react";
import { useI18n, useTranslate } from "@/lib/i18n";
import { ApiError, publicApiFetch } from "@/lib/api/client";
import { endpoints } from "@/lib/api/endpoints";
import type { PublicDispatchTrackingView } from "@/lib/api/dispatchTypes";
import { countedKey } from "@/lib/i18n/plural";
import { clockTime, isolate } from "@/modules/pos/lib/dispatchBoard";
import {
  hasMapPoints,
  isEndedStage,
  isTrackingTokenShape,
  isTrackingView,
  nextTrackingRead,
  telHref,
  timelineSteps,
  trackingMapPoints,
  type TimelineStep,
  type TrackingReadOutcome,
} from "./trackingView";

/* Leaflet loads only when a map is drawn (plan D-27). */
const TrackingMap = lazy(() => import("./TrackingMap"));

type PageState =
  | { kind: "loading" }
  | { kind: "ready"; view: PublicDispatchTrackingView; receivedAt: number; retrying: boolean }
  | { kind: "retrying" }
  | { kind: "notFound" };

function outcomeOf(error: unknown): TrackingReadOutcome {
  if (error instanceof ApiError) {
    if (error.status === 404) return { kind: "notFound" };
    if (error.status === 429) return { kind: "rateLimited" };
  }
  return { kind: "failed" };
}

/**
 * THE CUSTOMER'S DELIVERY TRACKING PAGE (driver backend B4 · P16, plan D-27):
 * `restaurant.<base>/:locale/track/:token`, the link the restaurant shares on
 * WhatsApp. No login: it sits outside `ProtectedLayout`, beside the driver
 * invite page, and reads ONE thing — the public tracking answer — through
 * `publicApiFetch` (no cookie, no referrer). It never reads the session.
 *
 * What it shows is what that answer carries and nothing else: the stage, the
 * restaurant, the order number, the timeline, and — only while the bag is on
 * its way — the driver's first name, a call button and the live dot, plus the
 * delivery pin, route line and ETA when present. The customer's name, phone
 * and address text are never in the answer, so they are never on the page.
 *
 * Reading: every 15 s while the order moves; it stops once DELIVERED or
 * NOT_DELIVERED, on a 404 (unknown / expired — the not-found state), backs off
 * on 429 and on failures (`nextTrackingRead`), and skips reads while the tab
 * is hidden (one read on return). A token that cannot be one of ours is
 * not-found without a request. A scoped exception to the staff app's
 * no-polling rule: a public page with no realtime channel (plan D-27).
 */
export function DeliveryTrackingPage() {
  const t = useTranslate();
  const { locale } = useI18n();
  const { token = "" } = useParams();
  const validToken = isTrackingTokenShape(token);
  const [state, setState] = useState<PageState>(() =>
    validToken ? { kind: "loading" } : { kind: "notFound" },
  );

  useEffect(() => {
    if (!validToken) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let controller: AbortController | null = null;
    let failures = 0;
    let dueWhileHidden = false;

    const schedule = (delayMs: number) => {
      timer = setTimeout(() => {
        timer = null;
        if (document.visibilityState === "hidden") {
          dueWhileHidden = true;
          return;
        }
        void read();
      }, delayMs);
    };

    const read = async () => {
      controller = new AbortController();
      let outcome: TrackingReadOutcome;
      try {
        const view = await publicApiFetch<PublicDispatchTrackingView>(
          endpoints.restaurant.publicDispatchTracking(token),
          { signal: controller.signal },
        );
        if (cancelled) return;
        if (!isTrackingView(view)) throw new TypeError("not a tracking answer");
        failures = 0;
        outcome = { kind: "ok", stage: view.stage };
        setState({ kind: "ready", view, receivedAt: Date.now(), retrying: false });
      } catch (error) {
        if (cancelled) return;
        outcome = outcomeOf(error);
        if (outcome.kind === "notFound") {
          setState({ kind: "notFound" });
        } else {
          failures += 1;
          // Keep the last answer on screen and say it is being retried.
          setState((previous) =>
            previous.kind === "ready" ? { ...previous, retrying: true } : { kind: "retrying" },
          );
        }
      }
      const next = nextTrackingRead(outcome, failures);
      if (!next.stop) schedule(next.delayMs);
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible" && dueWhileHidden) {
        dueWhileHidden = false;
        void read();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    void read();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      controller?.abort();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [token, validToken]);

  return (
    <main
      className="mx-auto flex min-h-svh w-full max-w-[560px] flex-col gap-4 bg-[color:var(--background)] px-4 pb-10 pt-5 text-[color:var(--text)]"
      data-testid="tracking-page"
      data-state={state.kind}
    >
      <meta name="referrer" content="no-referrer" />
      <meta name="robots" content="noindex,nofollow" />
      <title>{t((m) => m.dispatch.trackPageTitle)}</title>
      {state.kind === "notFound" ? (
        <NotFoundState />
      ) : state.kind === "ready" ? (
        <TrackingBody view={state.view} receivedAt={state.receivedAt} retrying={state.retrying} locale={locale} />
      ) : (
        <p
          role="status"
          className="mt-16 text-center text-[length:var(--font-size-sm)] font-bold text-[color:var(--text-muted)]"
          data-testid={state.kind === "retrying" ? "tracking-retrying" : "tracking-loading"}
        >
          {state.kind === "retrying" ? t((m) => m.dispatch.trackRetrying) : t((m) => m.dispatch.trackLoading)}
        </p>
      )}
    </main>
  );
}

function NotFoundState() {
  const t = useTranslate();
  return (
    <section className="mt-16 flex flex-col items-center gap-3 text-center" data-testid="tracking-not-found">
      <MapPinOff size={44} strokeWidth={1.6} aria-hidden className="text-[color:var(--text-muted)]" />
      <h1 className="text-[length:var(--font-size-lg)] font-extrabold">{t((m) => m.dispatch.trackNotFoundTitle)}</h1>
      <p className="max-w-[36ch] text-[length:var(--font-size-sm)] font-semibold text-[color:var(--text-secondary)]">
        {t((m) => m.dispatch.trackNotFoundBody)}
      </p>
    </section>
  );
}

/** The ETA line's key: the noun agrees with the range's upper bound. */
function etaKey(maxMinutes: number) {
  return countedKey(maxMinutes, {
    one: "trackEtaOne",
    two: "trackEtaTwo",
    few: "trackEtaFew",
    many: "trackEta",
  } as const);
}

const STAGE_TITLE = {
  PREPARING: "trackStagePreparing",
  ON_THE_WAY: "trackStageOnTheWay",
  ARRIVED: "trackStageArrived",
  DELIVERED: "trackStageDelivered",
  NOT_DELIVERED: "trackStageNotDelivered",
} as const;

const STEP_LABEL: Record<TimelineStep["key"], "trackStepPreparing" | "trackStepOnTheWay" | "trackStepArrived" | "trackStepDelivered"> = {
  preparing: "trackStepPreparing",
  onTheWay: "trackStepOnTheWay",
  arrived: "trackStepArrived",
  delivered: "trackStepDelivered",
};

function TrackingBody({
  view,
  receivedAt,
  retrying,
  locale,
}: {
  view: PublicDispatchTrackingView;
  receivedAt: number;
  retrying: boolean;
  locale: string;
}) {
  const t = useTranslate();
  const steps = timelineSteps(view);
  const points = useMemo(() => trackingMapPoints(view), [view]);
  const labels = useMemo(
    () => ({
      region: t((m) => m.dispatch.trackMapLabel),
      restaurant: t((m) => m.dispatch.trackMapRestaurant),
      destination: t((m) => m.dispatch.trackMapDestination),
      driver: t((m) => m.dispatch.trackMapDriver),
    }),
    [t],
  );
  const moving = view.stage === "ON_THE_WAY" || view.stage === "ARRIVED";
  const eta = view.eta;
  const call = view.driver ? telHref(view.driver.phone) : null;
  const ended = isEndedStage(view.stage);

  return (
    <>
      <header className="flex items-center gap-3" data-testid="tracking-restaurant">
        {view.restaurant.logoUrl ? (
          <img
            src={view.restaurant.logoUrl}
            alt=""
            referrerPolicy="no-referrer"
            className="size-11 shrink-0 rounded-full border border-[color:var(--border-light)] object-cover"
          />
        ) : null}
        <div className="min-w-0">
          <p className="truncate text-[length:var(--font-size-md)] font-extrabold">
            <bdi>{view.restaurant.name}</bdi>
          </p>
          <p className="text-[length:var(--font-size-xs)] font-bold tabular-nums text-[color:var(--text-muted)]" data-testid="tracking-order-number">
            {t((m) => m.dispatch.trackOrderNumber, { number: view.orderNumber })}
          </p>
        </div>
      </header>

      <section
        className="flex flex-col gap-1 rounded-[var(--radius-card,16px)] border border-[color:var(--border-light)] bg-[color:var(--surface)] px-4 py-4"
        aria-live="polite"
      >
        <h1
          className="text-[length:var(--font-size-xl)] font-extrabold leading-tight"
          data-testid="tracking-stage"
          data-stage={view.stage}
        >
          {t((m) => m.dispatch[STAGE_TITLE[view.stage]])}
        </h1>
        {eta && moving ? (
          <p className="flex items-center gap-1.5 text-[length:var(--font-size-sm)] font-bold text-[color:var(--text-secondary)]" data-testid="tracking-eta">
            <Clock size={16} aria-hidden />
            {t((m) => m.dispatch[etaKey(eta.maxMinutes)], {
              min: eta.minMinutes,
              max: eta.maxMinutes,
            })}
          </p>
        ) : null}
        {view.stage === "DELIVERED" ? (
          <p className="text-[length:var(--font-size-sm)] font-semibold text-[color:var(--text-secondary)]">
            {t((m) => m.dispatch.trackDeliveredBody)}
          </p>
        ) : view.stage === "NOT_DELIVERED" ? (
          <p className="text-[length:var(--font-size-sm)] font-semibold text-[color:var(--text-secondary)]">
            {t((m) => m.dispatch.trackNotDeliveredBody)}
          </p>
        ) : null}
      </section>

      {view.driver ? (
        <section
          className="flex items-center gap-3 rounded-[var(--radius-card,16px)] border border-[color:var(--border-light)] bg-[color:var(--surface)] px-4 py-3"
          data-testid="tracking-driver"
        >
          <span className="grid size-10 shrink-0 place-items-center rounded-full bg-[color:var(--info-100)] text-[color:var(--info)]">
            <Bike size={20} aria-hidden />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-[length:var(--font-size-2xs)] font-bold text-[color:var(--text-muted)]">
              {t((m) => m.dispatch.trackDriverTitle)}
            </p>
            <p className="truncate text-[length:var(--font-size-md)] font-extrabold" data-testid="tracking-driver-name">
              <bdi>{view.driver.firstName}</bdi>
            </p>
          </div>
          {call ? (
            <a
              href={call}
              className="inline-flex min-h-11 items-center gap-1.5 rounded-full bg-[color:var(--accent)] px-4 text-[length:var(--font-size-sm)] font-extrabold text-[color:var(--accent-contrast)]"
              data-testid="tracking-driver-call"
            >
              <Phone size={16} aria-hidden />
              {t((m) => m.dispatch.trackDriverCall, { name: isolate(view.driver.firstName) })}
            </a>
          ) : null}
        </section>
      ) : null}

      {moving && !view.driverPosition ? (
        <p className="text-[length:var(--font-size-xs)] font-semibold text-[color:var(--text-muted)]" data-testid="tracking-no-position">
          {t((m) => m.dispatch.trackNoPosition)}
        </p>
      ) : null}

      {!ended && hasMapPoints(points) ? (
        <Suspense fallback={<div className="h-[min(52svh,420px)] w-full rounded-[var(--radius-card,16px)] bg-[color:var(--surface-alt)]" />}>
          <TrackingMap points={points} labels={labels} />
        </Suspense>
      ) : null}

      <ol className="flex flex-col gap-0" data-testid="tracking-timeline">
        {steps.map((step, index) => (
          <li
            key={step.key}
            data-state={step.state}
            data-testid={`tracking-step-${step.key}`}
            className="flex items-start gap-3"
          >
            <span className="flex flex-col items-center self-stretch">
              <span
                className={[
                  "grid size-6 shrink-0 place-items-center rounded-full border-2",
                  step.state === "done"
                    ? "border-[color:var(--success)] bg-[color:var(--success)] text-[color:var(--accent-contrast)]"
                    : step.state === "current"
                      ? "border-[color:var(--accent)] bg-[color:var(--surface)] motion-safe:animate-pulse"
                      : "border-[color:var(--border-strong)] bg-[color:var(--surface)]",
                ].join(" ")}
              >
                {step.state === "done" ? <Check size={14} strokeWidth={3} aria-hidden /> : null}
              </span>
              {index < steps.length - 1 ? (
                <span className="w-[2px] flex-1 bg-[color:var(--border-light)]" aria-hidden />
              ) : null}
            </span>
            <span className="flex flex-1 items-baseline justify-between gap-2 pb-4">
              <span
                className={[
                  "text-[length:var(--font-size-sm)]",
                  step.state === "upcoming" ? "font-semibold text-[color:var(--text-muted)]" : "font-extrabold",
                ].join(" ")}
              >
                {t((m) => m.dispatch[STEP_LABEL[step.key]])}
              </span>
              {step.at && step.state !== "upcoming" ? (
                <span className="text-[length:var(--font-size-2xs)] font-bold tabular-nums text-[color:var(--text-muted)]">
                  {clockTime(step.at, locale)}
                </span>
              ) : null}
            </span>
          </li>
        ))}
      </ol>

      <p className="text-center text-[length:var(--font-size-2xs)] font-semibold text-[color:var(--text-muted)]" data-testid="tracking-updated">
        {retrying
          ? t((m) => m.dispatch.trackRetrying)
          : t((m) => m.dispatch.trackLastUpdated, { time: clockTime(new Date(receivedAt).toISOString(), locale) })}
      </p>
    </>
  );
}
